/**
 * Minimal, dependency-free JWT helpers for the Keycloak/OIDC bearer flow.
 *
 * We only ever *read* the access token client-side, and only for its expiry —
 * the backend is the sole authority that validates the signature and resolves
 * the user (via `Whoami`). So there is deliberately **no signature
 * verification here** — never trust these values for anything security-bearing.
 */

export interface JwtClaims {
  /** Subject — Keycloak user id (uuid). */
  sub?: string
  /** Expiry, seconds since epoch. */
  exp?: number
  /** Issued-at, seconds since epoch. */
  iat?: number
  // Realm-specific claims may show up too — keep it open.
  [claim: string]: unknown
}

/**
 * Base64url-decode + JSON-parse the payload segment of a JWT.
 * Returns `null` for anything that isn't a well-formed three-part token.
 */
export function decodeJwt(token: string): JwtClaims | null {
  if (!token) {
    return null
  }
  const parts = token.split('.')
  if (parts.length !== 3) {
    return null
  }
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '=',
    )
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    )
    return JSON.parse(json) as JwtClaims
  } catch {
    return null
  }
}

/** Milliseconds until the token expires. Negative/zero means already expired. */
export function millisUntilExpiry(token: string): number {
  const claims = decodeJwt(token)
  if (!claims?.exp) {
    return 0
  }
  return claims.exp * 1000 - Date.now()
}

/** True when the token has no usable `exp` or that `exp` is in the past. */
export function isJwtExpired(token: string): boolean {
  return millisUntilExpiry(token) <= 0
}
